// KycFlowPage — applicant-facing KYC mockup.
// Wrapped in OnboardingShell so it matches the rest of the onboarding flow:
//   left rail = locked stage list, right rail = active step form.
// Sub-steps within the KYC stage render as a horizontal stepper at the top of
// the right rail.

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, ShieldCheck, Camera, FileImage, Building2, Check, ChevronRight, AlertCircle, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OnboardingShell } from "@/components/shells/OnboardingShell";
import { getKycContext, completeKyc } from "@/lib/onboarding-api";
import { toast } from "sonner";

const STEPS = [
  { id: "identity", title: "Confirm identity",  icon: ShieldCheck },
  { id: "id",       title: "Upload ID",          icon: FileImage },
  { id: "selfie",   title: "Selfie liveness",    icon: Camera },
  { id: "bank",     title: "Add bank account",   icon: Building2 },
];

export default function KycFlowPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Form state — purely mockup.
  const [idType, setIdType] = useState("DriversLicense");
  const [idNumber, setIdNumber] = useState("");
  const [selfieDone, setSelfieDone] = useState(false);
  const [routing, setRouting] = useState("");
  const [accountLast4, setAccountLast4] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const c = await getKycContext(token);
        setCtx(c);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const next = () => setStepIdx((i) => Math.min(STEPS.length - 1, i + 1));
  const back = () => setStepIdx((i) => Math.max(0, i - 1));

  const submit = async () => {
    setSubmitting(true);
    try {
      await completeKyc(token, {
        idType,
        idNumber,
        idCountry:    "US",
        selfieCaptured:   true,
        addressConfirmed: true,
        bankRoutingLast4: routing.slice(-4),
        bankAccountLast4: accountLast4,
      });
      navigate(`/onboarding/kyc/${token}/complete`, { replace: true });
    } catch (e) {
      toast.error("KYC submission failed", { description: e.message });
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <OnboardingShell currentStage="kyc">
        <div className="min-h-[320px] grid place-items-center text-ink-muted">
          <span><Loader2 className="size-5 animate-spin mr-2 inline" /> Loading…</span>
        </div>
      </OnboardingShell>
    );
  }

  if (error) {
    return (
      <OnboardingShell currentStage="kyc">
        <div className="max-w-md">
          <AlertCircle className="size-10 text-red-600 mb-3" />
          <h1 className="font-brand text-h1 text-ink">Link expired or invalid</h1>
          <p className="mt-2 text-body-sm text-ink-muted">{error}</p>
          <p className="mt-4 text-caption text-ink-subtle">Please contact support if you believe this is an error.</p>
        </div>
      </OnboardingShell>
    );
  }

  if (ctx.status === "Active") {
    return (
      <OnboardingShell currentStage="active">
        <div className="max-w-md">
          <Check className="size-12 text-emerald-600 mb-3" />
          <h1 className="font-brand text-h1 text-ink">KYC already completed</h1>
          <p className="mt-2 text-body-sm text-ink-muted">Check your email — your login credentials have already been sent.</p>
          <Button onClick={() => navigate("/login")} className="mt-5 bg-brand-700 hover:bg-brand-800 text-white">Go to sign-in</Button>
        </div>
      </OnboardingShell>
    );
  }

  const step = STEPS[stepIdx];
  const isLast = stepIdx === STEPS.length - 1;
  const canAdvance = (
    (step.id === "identity") ||
    (step.id === "id"      && idNumber.length >= 4) ||
    (step.id === "selfie"  && selfieDone) ||
    (step.id === "bank"    && routing.length >= 9 && accountLast4.length === 4)
  );

  return (
    <OnboardingShell currentStage="kyc">
      <div data-testid="kyc-flow">
        <div className="eyebrow mb-3 text-brand-700">Identity check</div>
        <h1 className="font-brand text-[40px] lg:text-[44px] leading-[1.05] tracking-[-0.025em] text-ink font-bold">
          Hi {ctx.firstName}, let&rsquo;s verify your identity.
        </h1>
        <p className="mt-5 text-body text-ink-muted leading-relaxed max-w-xl">
          Four quick steps. None of this leaves our servers in plain text — IDs are encrypted at rest with AWS KMS.
        </p>

        {/* Sub-step pills */}
        <ol className="mt-9 flex items-center gap-2 flex-wrap" data-testid="kyc-substeps">
          {STEPS.map((s, i) => {
            const done = i < stepIdx;
            const active = i === stepIdx;
            return (
              <li key={s.id} className="flex items-center gap-2 min-w-0">
                <div className={`size-7 rounded-full grid place-items-center text-caption font-bold shrink-0 ${
                  done ? "bg-brand-700 text-white"
                       : active ? "bg-ink text-white"
                                : "bg-surface-alt text-ink-hint border border-line"
                }`}>
                  {done ? <Check className="size-3.5" /> : i + 1}
                </div>
                <span className={`text-caption truncate ${active ? "text-ink font-semibold" : done ? "text-ink-muted" : "text-ink-hint"}`}>
                  {s.title}
                </span>
                {i < STEPS.length - 1 && <div className={`w-8 h-px ${done ? "bg-brand-700" : "bg-line"}`} />}
              </li>
            );
          })}
        </ol>

        <div className="mt-9 max-w-2xl">
          {step.id === "identity" && (
            <Section title="Confirm your identity" sub="This is what we received with your application. Anything to update?">
              <dl className="grid grid-cols-2 gap-x-8 gap-y-5 text-body-sm">
                <KV label="Full name" value={`${ctx.firstName} ${ctx.lastName}`} />
                <KV label="Email" value={ctx.email} mono />
                <KV label="Address" value={`${ctx.address.street}, ${ctx.address.city}, ${ctx.address.state} ${ctx.address.zip}`} className="col-span-2" />
              </dl>
              <p className="mt-6 text-caption text-ink-subtle">If anything&rsquo;s incorrect, reply to the email we sent and we&rsquo;ll fix it on our end.</p>
            </Section>
          )}

          {step.id === "id" && (
            <Section title="Upload a government ID" sub="We support US driver's licenses and passports. (Mockup — no file actually uploads.)">
              <div className="flex gap-2 mb-5">
                {[
                  { id: "DriversLicense", label: "Driver's license" },
                  { id: "Passport",       label: "Passport" },
                ].map((t) => (
                  <button key={t.id} type="button" onClick={() => setIdType(t.id)}
                          className={`px-4 py-2 rounded-md border text-body-sm font-medium transition-all ${
                            idType === t.id ? "bg-brand-50 border-brand-700 text-ink" : "bg-surface border-line text-ink-muted hover:border-ink"
                          }`}>
                    {t.label}
                  </button>
                ))}
              </div>
              <div className="border-2 border-dashed border-line rounded-lg p-10 text-center bg-surface-alt/30">
                <FileImage className="size-10 text-ink-hint mx-auto" />
                <p className="mt-3 text-body-sm text-ink">Drop your {idType === "DriversLicense" ? "driver's license" : "passport"} here</p>
                <p className="text-caption text-ink-subtle">JPG or PDF · up to 10 MB · this is a mockup</p>
              </div>
              <div className="mt-5">
                <Label className="text-eyebrow uppercase text-ink-muted mb-1.5 block">
                  {idType === "DriversLicense" ? "License number" : "Passport number"}
                </Label>
                <Input value={idNumber} onChange={(e) => setIdNumber(e.target.value.toUpperCase())} data-testid="id-number-input"
                       placeholder="ABC1234567" className="max-w-xs font-mono h-12 bg-surface" />
              </div>
            </Section>
          )}

          {step.id === "selfie" && (
            <Section title="Selfie liveness" sub="A 5-second video selfie to match against your ID. (Mockup — no camera will activate.)">
              <div className="bg-ink/95 rounded-lg aspect-video grid place-items-center text-white relative overflow-hidden">
                {selfieDone ? (
                  <div className="text-center">
                    <Check className="size-12 mx-auto text-emerald-400 mb-2" />
                    <p className="text-body-sm">Selfie captured · liveness confirmed</p>
                  </div>
                ) : (
                  <div className="text-center">
                    <Camera className="size-12 mx-auto opacity-50 mb-3" />
                    <p className="text-body-sm opacity-80">Camera preview placeholder</p>
                  </div>
                )}
              </div>
              <Button onClick={() => setSelfieDone(true)} disabled={selfieDone} data-testid="capture-selfie-btn"
                      className="mt-5 bg-brand-700 hover:bg-brand-800 text-white">
                {selfieDone ? <><Check className="size-4 mr-1.5" /> Captured</> : "Capture selfie"}
              </Button>
            </Section>
          )}

          {step.id === "bank" && (
            <Section title="Add your bank account" sub="ACH payouts hit this account once your cards are approved. We never share or store the full account number unencrypted.">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-eyebrow uppercase text-ink-muted mb-1.5 block">Routing number</Label>
                  <Input value={routing} onChange={(e) => setRouting(e.target.value.replace(/\D/g, "").slice(0, 9))}
                         data-testid="bank-routing-input"
                         placeholder="9 digits" className="h-12 font-mono bg-surface" />
                </div>
                <div>
                  <Label className="text-eyebrow uppercase text-ink-muted mb-1.5 block">Account number — last 4</Label>
                  <Input value={accountLast4} onChange={(e) => setAccountLast4(e.target.value.replace(/\D/g, "").slice(0, 4))}
                         data-testid="bank-last4-input"
                         placeholder="0000" className="h-12 font-mono bg-surface" />
                  <p className="mt-1.5 text-caption text-ink-hint">For demo purposes only — last 4 is enough.</p>
                </div>
              </div>
              <div className="mt-5 bg-emerald-50 border border-emerald-200 rounded-md p-3 text-caption text-ink-muted">
                <strong className="text-ink">Almost done.</strong> Submitting this step will activate your account and we&rsquo;ll email your login credentials immediately.
              </div>
            </Section>
          )}

          <div className="mt-8 pt-5 border-t border-line flex items-center justify-between gap-3">
            <Button variant="outline" onClick={back} disabled={stepIdx === 0 || submitting}>
              <ArrowLeft className="size-4 mr-1.5" /> Back
            </Button>
            {isLast ? (
              <Button onClick={submit} disabled={!canAdvance || submitting} data-testid="kyc-finish-btn"
                      className="bg-brand-700 hover:bg-brand-800 text-white h-12 px-6">
                {submitting ? <><Loader2 className="size-4 mr-1.5 animate-spin" /> Finalising…</> : <>Activate account <ChevronRight className="size-4 ml-1" /></>}
              </Button>
            ) : (
              <Button onClick={next} disabled={!canAdvance} data-testid="kyc-next-btn"
                      className="bg-ink hover:bg-ink/90 text-white h-12 px-6">
                Continue <ChevronRight className="size-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </OnboardingShell>
  );
}

function Section({ title, sub, children }) {
  return (
    <div>
      <h2 className="font-brand text-h2 text-ink font-semibold">{title}</h2>
      <p className="mt-1 text-body-sm text-ink-muted leading-relaxed">{sub}</p>
      <div className="mt-6">{children}</div>
    </div>
  );
}

function KV({ label, value, mono, className = "" }) {
  return (
    <div className={className}>
      <dt className="text-caption text-ink-hint">{label}</dt>
      <dd className={`mt-0.5 text-ink ${mono ? "font-mono text-code" : ""}`}>{value}</dd>
    </div>
  );
}
